'use client';

import { useState } from 'react';
import { 
  CheckCircle, 
  XCircle, 
  MessageSquare,
  Clock,
  Filter,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import {
  usePendingApprovals,
  useApproveDecision,
  type Approval,
  type ArtifactType,
} from '@/hooks/use-approvals';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface ApprovalDashboardProps {
  projectId: string;
  projectName?: string;
}

const ARTIFACT_TYPE_LABELS: Record<ArtifactType, string> = {
  baseSpec: 'Base Spec',
  contract: 'Contract',
  technicalSpec: 'Technical Spec',
  release: 'Release',
  goal: 'Goal',
  persona: 'Persona',
  useCase: 'Use Case',
  journey: 'Journey',
  serviceMap: 'Service Map',
  storyboard: 'Storyboard',
};

const STATUS_CONFIG = {
  PENDING: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  APPROVED: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  REJECTED: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: XCircle },
  CHANGES_REQUESTED: { label: 'Changes Requested', color: 'bg-orange-100 text-orange-800', icon: AlertCircle },
};

export function ApprovalDashboard({ projectId, projectName = 'Project' }: ApprovalDashboardProps) {
  const [selectedApproval, setSelectedApproval] = useState<Approval | null>(null);
  const [decisionDialogOpen, setDecisionDialogOpen] = useState(false);
  const [decision, setDecision] = useState<'APPROVED' | 'REJECTED' | 'CHANGES_REQUESTED'>('APPROVED');
  const [comments, setComments] = useState('');
  const [artifactTypeFilter, setArtifactTypeFilter] = useState<ArtifactType | 'all'>('all');
  
  const { toast } = useToast();
  const { data: pendingApprovals, isLoading } = usePendingApprovals(projectId);
  const approveMutation = useApproveDecision(projectId);

  const handleOpenDecisionDialog = (approval: Approval) => {
    setSelectedApproval(approval);
    setDecision('APPROVED');
    setComments('');
    setDecisionDialogOpen(true);
  };

  const handleMakeDecision = async () => {
    if (!selectedApproval) return;

    try {
      await approveMutation.mutateAsync({
        approvalId: selectedApproval.id,
        decision: {
          status: decision,
          comments: comments || undefined,
        },
      });
      toast({ 
        title: 'Decision recorded', 
        description: `Approval ${decision.toLowerCase()} successfully.` 
      });
      setDecisionDialogOpen(false);
      setSelectedApproval(null);
      setComments('');
    } catch (error) {
      toast({ 
        title: 'Decision failed', 
        description: error instanceof Error ? error.message : 'Failed to process approval decision',
        variant: 'destructive',
      });
    }
  };

  const filteredApprovals = artifactTypeFilter === 'all' 
    ? pendingApprovals || []
    : (pendingApprovals || []).filter(a => a.artifactType === artifactTypeFilter);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="border-b bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Approval Dashboard</h1>
            <p className="text-sm text-gray-600 mt-1">Review and approve artifacts for {projectName}</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={artifactTypeFilter} onValueChange={(v) => setArtifactTypeFilter(v as ArtifactType | 'all')}>
              <SelectTrigger className="w-[180px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {Object.entries(ARTIFACT_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {!pendingApprovals || filteredApprovals.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <CheckCircle className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Approvals</h3>
            <p className="text-gray-600">All artifacts are up to date. Check back later for new approvals.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredApprovals.map((approval) => {
              const artifactTypeLabel = ARTIFACT_TYPE_LABELS[approval.artifactType] || approval.artifactType;
              const statusConfig = STATUS_CONFIG[approval.status];
              const StatusIcon = statusConfig.icon;
              
              return (
                <Card key={approval.id} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <CardTitle className="text-lg">
                            {approval.artifact?.title || `Artifact ${approval.artifactId.substring(0, 8)}`}
                          </CardTitle>
                          <Badge className={cn(statusConfig.color, 'flex items-center gap-1')}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig.label}
                          </Badge>
                          <Badge variant="outline">{artifactTypeLabel}</Badge>
                        </div>
                        {approval.artifact?.version && (
                          <CardDescription>Version {approval.artifact.version}</CardDescription>
                        )}
                        {approval.comments && (
                          <p className="text-sm text-gray-600 mt-2">{approval.comments}</p>
                        )}
                      </div>
                      {approval.status === 'PENDING' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenDecisionDialog(approval)}
                        >
                          <MessageSquare className="w-4 h-4 mr-2" />
                          Review
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <span>Requested by: {approval.approver.name || approval.approver.email}</span>
                      <span>{new Date(approval.createdAt).toLocaleDateString()}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Decision Dialog */}
      <Dialog open={decisionDialogOpen} onOpenChange={setDecisionDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Review Approval</DialogTitle>
          </DialogHeader>
          {selectedApproval && (
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">
                  {selectedApproval.artifact?.title || 'Artifact'}
                </h4>
                <p className="text-sm text-gray-600">
                  Type: {ARTIFACT_TYPE_LABELS[selectedApproval.artifactType] || selectedApproval.artifactType}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Decision</label>
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    type="button"
                    variant={decision === 'APPROVED' ? 'default' : 'outline'}
                    onClick={() => setDecision('APPROVED')}
                    className="flex items-center justify-center"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                  <Button
                    type="button"
                    variant={decision === 'REJECTED' ? 'destructive' : 'outline'}
                    onClick={() => setDecision('REJECTED')}
                    className="flex items-center justify-center"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                  <Button
                    type="button"
                    variant={decision === 'CHANGES_REQUESTED' ? 'default' : 'outline'}
                    onClick={() => setDecision('CHANGES_REQUESTED')}
                    className="flex items-center justify-center"
                  >
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Request Changes
                  </Button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Comments</label>
                <Textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  rows={4}
                  placeholder="Add comments about your decision..."
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleMakeDecision}
              disabled={approveMutation.isPending}
              variant={decision === 'APPROVED' ? 'default' : decision === 'REJECTED' ? 'destructive' : 'default'}
            >
              {approveMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {decision === 'APPROVED' && <CheckCircle className="w-4 h-4 mr-2" />}
                  {decision === 'REJECTED' && <XCircle className="w-4 h-4 mr-2" />}
                  {decision === 'CHANGES_REQUESTED' && <AlertCircle className="w-4 h-4 mr-2" />}
                  Submit Decision
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
